import "../../styles/animation-circle.css";

import React, { useEffect, useRef } from "react";
import { ThemeProvider, useTheme, Box, Button } from "@mui/material";
import mainTheme from "../../theme.tsx";
import gsap from "gsap";

import CirclePanelAnimation from "./CirclePanelAnimation.tsx";
import { Trans } from "@lingui/react/macro";

export const TYPE_ANIMATION = 1.8;

interface CircleProps {
  children?: React.ReactNode;
}

const CirclePanel: React.FC<CircleProps> = ({ children }) => {
  const size = `min(94vw, 94vh)`;
  const ctRef = useRef<gsap.Context | null>(null);
  const startTl2 = useRef<(delay: number) => void>(() => {});
  const childTimelineRef = useRef<gsap.core.Timeline | null>(null);
  const [isSkipVisible, setIsSkipVisible] = React.useState(true);
  const theme = useTheme();

  useEffect(() => {
    let ctx = gsap.context(() => {
      const t_1 = document.getElementById("t_1") as SVGPathElement | null;
      const t_1Length = t_1 ? t_1.getTotalLength() : 0;
      const t_2 = document.getElementById("t_2") as SVGPathElement | null;
      const t_2Length = t_2 ? t_2.getTotalLength() + 1 : 0;
      const a_1 = document.getElementById("a_1") as SVGPathElement | null;
      const a_1Length = a_1 ? a_1.getTotalLength() : 0;
      const a_2 = document.getElementById("a_2") as SVGPathElement | null;
      const a_2Length = a_2 ? a_2.getTotalLength() + 10 : 0;
      const l_1 = document.getElementById("l_1") as SVGPathElement | null;
      const l_1Length = l_1 ? l_1.getTotalLength() : 0;
      const l_2 = document.getElementById("l_2") as SVGPathElement | null;
      const l_2Length = l_2 ? l_2.getTotalLength() + 10 : 0;
      const y_1 = document.getElementById("y_1") as SVGPathElement | null;
      const y_1Length = y_1 ? y_1.getTotalLength() : 0;
      const y_2 = document.getElementById("y_2") as SVGPathElement | null;
      const y_2Length = y_2 ? y_2.getTotalLength() : 0;

      const tl = gsap.timeline({
        delay: 0.5,
        defaults: { ease: "power1.inOut", duration: 0.5 },
        onComplete: () => {
          startTl2.current?.(1);
        },
      });

      startTl2.current = (delay: number) => {
        const isSafari = /^((?!chrome|android).)*safari/i.test(
          navigator.userAgent,
        );

        const tl = gsap.timeline({
          delay: isSafari ? delay * 5 : delay * 9,
          defaults: { ease: "power1.inOut", duration: 0.6 },
        });

        if (!isSafari) {
          tl.to(t_2, { strokeDashoffset: -t_2Length }, "<");
          tl.to(t_1, { strokeDashoffset: -t_1Length }, "<0.2");
          tl.to(a_1, { strokeDashoffset: -a_1Length }, "<0.2");
          tl.to(a_2, { strokeDashoffset: -a_2Length }, "<");
          tl.to(l_1, { strokeDashoffset: -l_1Length }, "<0.2");
          tl.to(l_2, { strokeDashoffset: -l_2Length }, "<0.2");
          tl.to(y_1, { strokeDashoffset: -y_1Length }, "<0.2");
          tl.to(y_2, { strokeDashoffset: -y_2Length }, "<0.05");
          tl.duration(1);
          tl.set(".tally-canvas", { autoAlpha: 0 });
        } else {
          tl.to(".logo__type", { autoAlpha: 0 });
          tl.set(".tally-canvas", { autoAlpha: 0 });
        }
        tl.fromTo(
          ".interface__dot",
          {
            xPercent: -50,
            yPercent: -352,
          },
          {
            xPercent: -50,
            yPercent: -50,
            overwrite: "auto",
            duration: 1,
            ease: "power3.out",
          },
          "<",
        );
        tl.to(
          ".interface__dot",
          {
            width: "min(94vw, 94vh)",
            height: "min(94vw, 94vh)",
            left: "50%",
            top: "50%",
            duration: 1.4,
            ease: "power3.inOut",
          },
          "<",
        );
        tl.fromTo(
          ".interface__content",
          { autoAlpha: 0, duration: 1.2, ease: "power3.inOut" },
          { autoAlpha: 1 },
          ">-0.4",
        );

        setTimeout(
          () => {
            setIsSkipVisible(false);
          },
          isSafari ? 5000 : 9000,
        );
      };

      tl.set(".logo", { autoAlpha: 1 });
      tl.fromTo(".logo__dot", { scale: 0 }, { scale: 1 });
      tl.fromTo(
        t_1,
        { strokeDashoffset: t_1Length, strokeDasharray: t_1Length },
        { strokeDashoffset: 0 },
        "<",
      );
      tl.fromTo(
        t_2,
        { strokeDashoffset: t_2Length, strokeDasharray: t_2Length },
        { strokeDashoffset: 0 },
        "<0.2",
      );
      tl.fromTo(
        a_1,
        { strokeDashoffset: a_1Length, strokeDasharray: a_1Length },
        { strokeDashoffset: 0 },
        "<0.2",
      );
      tl.set(a_2, { opacity: 1 }, "<0.3");
      tl.fromTo(
        a_2,
        { strokeDashoffset: a_2Length, strokeDasharray: a_2Length },
        { strokeDashoffset: 0 },
        "<",
      );
      tl.fromTo(
        l_1,
        { strokeDashoffset: l_1Length, strokeDasharray: l_1Length },
        { strokeDashoffset: 0 },
        "<0.2",
      );
      tl.fromTo(
        l_2,
        { strokeDashoffset: l_2Length, strokeDasharray: l_2Length },
        { strokeDashoffset: 0 },
        "<0.2",
      );
      tl.fromTo(
        y_1,
        { strokeDashoffset: y_1Length, strokeDasharray: y_1Length },
        { strokeDashoffset: 0 },
        "<0.2",
      );
      tl.fromTo(
        y_2,
        { strokeDashoffset: y_2Length, strokeDasharray: y_2Length },
        { strokeDashoffset: 0 },
        "<0.05",
      );
      tl.duration(TYPE_ANIMATION);
    });

    ctRef.current = ctx;

    return () => {
      ctRef.current?.revert();
      ctRef.current = null;
    };
  }, []);

  const handleStop = () => {
    // Complete the child timeline
    if (childTimelineRef.current) {
      childTimelineRef.current.set(".interface__dot", { opacity: 1 }, ">-=0.8");
    } else {
      console.warn("Child timeline not found!");
    }
    ctRef.current?.revert();
    // Ensure the `onComplete` logic is triggered
    startTl2.current?.(0);
    setTimeout(() => {
      setIsSkipVisible(false); // Hide the button after 2 seconds
    }, 2000); // 2000 milliseconds = 2 seconds
  };

  return (
    <ThemeProvider theme={mainTheme}>
      <div className="logo">
        <div className="logo__dot"></div>
        <svg
          className="logo__type"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          viewBox="0 0 890.9 522.1"
        >
          <g>
            <defs>
              <path
                id="t_1-mask"
                d="M65.1,0h41.7v90.5h60.2v35.4h-60.2v141.6c0,51.2,14.8,63.4,50.7,63.4c5.3,0,11.6-0.5,11.6-0.5v37
			c0,0-11.1,1.6-25.4,1.6c-54.9,0-78.7-35.9-78.7-97.8L65.1,0L65.1,0z"
              />
            </defs>
            <clipPath id="t_1-clip-path">
              <use xlinkHref="#t_1-mask" />
            </clipPath>

            <path
              id="t_1"
              d="M77.4-26.1c0,0,1.3,246.7,1.3,278.4s-0.3,116.3,117.8,98.8"
            />
          </g>
          <g>
            <defs>
              <path
                id="t_2-mask"
                d="M65.1,0h41.7v90.5h60.2v35.4h-60.2v141.6
			c0,51.2,14.8,63.4,50.7,63.4c5.3,0,11.6-0.5,11.6-0.5v37c0,0-11.1,1.6-25.4,1.6c-54.9,0-78.7-35.9-78.7-97.8L65.1,0L65.1,0z"
              />
            </defs>
            <clipPath id="t_2-clip-path">
              <use xlinkHref="#t_2-mask" />
            </clipPath>

            <path id="t_2" d="M65.1 108.1 L167.1 108.1" />
          </g>
          <g>
            <defs>
              <path
                id="a_1-mask"
                d="M298.6,340.9c40.1,0,68.2-23.8,68.2-57.1s-27.5-56.5-68.7-56.5s-68.7,24.3-68.7,56.5
			S256.3,340.9,298.6,340.9 M292.8,194.1c34.9,0,61.3,13.2,72.4,24.8v-33.8c0-38.6-25.9-60.8-66.6-60.8c-33.8,0-61.3,12.1-80.8,23.8
			l-16.9-30.7c18.5-11.6,54.9-30.1,101.4-30.1c75,0,104.6,41.7,104.6,100.4v181.2H371l-4.8-23.2c-14.3,16.4-37.5,30.1-74.5,30.1
			c-60.2,0-104.6-38-104.6-91.4S231,194,292.8,194.1"
              />
            </defs>
            <clipPath id="a_1-clip-path">
              <use xlinkHref="#a_1-mask" />
            </clipPath>

            <path
              id="a_1"
              d="M204.9,136.1c0,0,73.9-57.7,146.3-20.4c27.1,30.6,38.5,34.7,38.5,64.5s2,198.8,2,198.8"
            />
          </g>
          <g>
            <defs>
              <path
                id="a_2-mask"
                d="M298.6,340.9c40.1,0,68.2-23.8,68.2-57.1s-27.5-56.5-68.7-56.5s-68.7,24.3-68.7,56.5
			S256.3,340.9,298.6,340.9 M292.8,194.1c34.9,0,61.3,13.2,72.4,24.8v-33.8c0-38.6-25.9-60.8-66.6-60.8c-33.8,0-61.3,12.1-80.8,23.8
			l-16.9-30.7c18.5-11.6,54.9-30.1,101.4-30.1c75,0,104.6,41.7,104.6,100.4v181.2H371l-4.8-23.2c-14.3,16.4-37.5,30.1-74.5,30.1
			c-60.2,0-104.6-38-104.6-91.4S231,194,292.8,194.1"
              />
            </defs>
            <clipPath id="a_2-clip-path">
              <use xlinkHref="#a_2-mask" />
            </clipPath>

            <path
              id="a_2"
              d="M387.7,279.4c0,0-7.9-68.6-116.9-68.6c-43.8,0-61.5,40-61.5,78.1s30.2,68.3,84.9,68.3S387.7,325.4,387.7,279.4z"
            />
          </g>
          <g>
            <defs>
              <rect id="l_1-mask" x="461.7" width="43" height="368.9" />
            </defs>
            <clipPath id="l_1-clip-path">
              <use xlinkHref="#l_1-mask" />
            </clipPath>

            <path id="l_1" d="M483.2 -4.6 L483.2 373.7" />
          </g>
          <g>
            <defs>
              <rect id="l_2-mask" x="552.1" width="43" height="368.9" />
            </defs>
            <clipPath id="l_2-clip-path">
              <use xlinkHref="#l_2-mask" />
            </clipPath>

            <path id="l_2" d="M573.6 -6.9 L573.6 368.9" />
          </g>
          <g>
            <defs>
              <polygon
                id="y_1-mask"
                points="877.4,90.8 730,460.1 688.3,460.1 727.9,362.9 615.9,90.8 661.3,90.8 748.5,311.6 833,90.8"
              />
            </defs>
            <clipPath id="y_1-clip-path">
              <use xlinkHref="#y_1-mask" />
            </clipPath>

            <path id="y_1" d="M634.8 78.4 L768.9 416.3" />
          </g>

          <g>
            <defs>
              <polygon
                id="y_2-mask"
                points="877.4,90.8 730,460.1 688.3,460.1 727.9,362.9 615.9,90.8 661.3,90.8 748.5,311.6 833,90.8"
              />
            </defs>
            <clipPath id="y_2-clip-path">
              <use xlinkHref="#y_2-mask" />
            </clipPath>

            <path id="y_2" d="M869 69.7 L704.9 487.2" />
          </g>
        </svg>
      </div>

      <CirclePanelAnimation timelineRef={childTimelineRef} />

      <div
        className="interface__dot"
        style={{
          opacity: 0,
          width: "min(1.5vw, 1.5vw)",
          height: "min(1.5vw, 1.5vw)",
          borderRadius: "50%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          position: "fixed",
          top: "50%",
          left: "37.88%",
          zIndex: -1,
          backgroundColor: `${theme.palette.secondary.main}`,
          flexDirection: "column",
        }}
      ></div>
      <div
        className="interface__content"
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          flexDirection: "column",
          opacity: 0,
          visibility: "hidden",
        }}
      >
        {children}
      </div>
      <Box
        sx={{
          position: "absolute",
          bottom: 20,
          width: "100%",
          display: "flex",
          justifyContent: {
            xs: "center",
            sm: "flex-end",
          },
          alignItems: "center",
          padding: "16px",
          zIndex: 10,
          "@media (max-width: 600px)": {
            left: 0,
            right: 0,
          },
        }}
      >
        <Button
          variant="outlined"
          sx={{
            padding: "7px 7px 2px 7px",
            backgroundColor: theme.palette.secondary.main,
            color: "black",
            fontSize: "14px",
            borderRadius: "6px",
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(0, 0, 0, 0.2)",
            visibility: isSkipVisible ? "visible" : "hidden",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 1)",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
            },
          }}
          onClick={handleStop}
        >
          <Trans context={"Button for skipping the introductory animation."}>
            SKIP
          </Trans>
        </Button>
      </Box>
    </ThemeProvider>
  );
};

export default CirclePanel;
